import { getDevices } from "./api/users_{userId}_devices";
import type { Device } from "./device/device";
import { setLocale } from "./locale";
import type { Smartplug2 } from "./smartplug2/sp2";
import { sortedSetAdd } from "./sorted_set";
import * as Storage from "./storage";

class State {
	devicePollingInterval: NodeJS.Timeout | undefined;
	farmcamSims: Record<string, FarmcamSim> = {};
	fencealarm2s: Record<string, Fencealarm2> = {};
	fencealarms: Record<string, Fencealarm> = {};

	getActiveUser = async (): Promise<User> => {
		const impersonate = sessionStorage.getItem("impersonate");
		if (impersonate) return JSON.parse(impersonate);
		return this.getSignedInUser();
	};

	getDeviceTags = (): string[] => {
		const tagSet: Set<string> = new Set();
		const devices: [string, Device][] = [
			...Object.entries(this.farmcamSims),
			...Object.entries(this.fencealarm2s),
			...Object.entries(this.fencealarms),
			...Object.entries(this.smartplug2s),
			...Object.entries(this.smartplugs),
		];

		devices
			.flatMap(([_id, fcs]) => fcs.Metadata.Tags)
			.forEach((tag) => {
				tagSet.add(tag);
			});

		return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
	};

	getSignedInUser = async (): Promise<User> => {
		const user = await Storage.getItem("user");
		if (user) return JSON.parse(user);
		return {
			Id: "",
			CustomerId: "",
			AccountType: "",
			Email: "",
			Phone: "",
			Locale: "en-GB",
			Country: "",
			EmailNotifications: {
				Billing: false,
				Device: false,
				Marketing: false,
			},
			PushNotifications: {
				Enabled: false,
				Tokens: [],
			},
			SmsNotifications: {
				Enabled: false,
				Balance: 0,
			},
		};
	};

	getToken = async (): Promise<Token> => {
		const token = await Storage.getItem("token");
		if (token) return JSON.parse(token);
		else
			return {
				String: "",
				ExpiresAt: "1970-01-01T00:00:00Z",
			};
	};

	getTokenString = async (): Promise<string> => {
		const token = await this.getToken();
		return token.String;
	};

	isImpersonating = (): boolean => {
		const impersonate = sessionStorage.getItem("impersonate");
		return !!impersonate;
	};

	isSignedIn = async (): Promise<boolean> => {
		const margin = 1e4;
		const token = await this.getToken();
		const expiresAt = new Date(token.ExpiresAt);
		const now = Date.now();
		return now < expiresAt.getTime() - margin;
	};

	loadUserLocale = async (): Promise<void> => {
		const user = await this.getSignedInUser();
		setLocale(user.Locale);
	};

	refreshDevices = async (): Promise<void> => {
		const devices = await getDevices();

		Object.keys(this.farmcamSims).forEach((imsi) => {
			delete this.farmcamSims[imsi];
		});

		for (const fcs of devices.FarmcamSims) {
			fcs.Metadata.Tags = sortedSetAdd(fcs.Metadata.Tags, "FarmCam SIM");
			fcs.Metadata.Tags = sortedSetAdd(
				fcs.Metadata.Tags,
				fcs.Metadata.OwnerEmail,
			);
			this.farmcamSims[fcs.Imsi] = fcs;
		}

		Object.keys(this.fencealarms).forEach((imei) => {
			delete this.fencealarms[imei];
		});

		for (const fa of devices.Fencealarms) {
			fa.Metadata.Tags = sortedSetAdd(fa.Metadata.Tags, "FenceAlarm");
			fa.Metadata.Tags = sortedSetAdd(
				fa.Metadata.Tags,
				fa.Metadata.OwnerEmail,
			);
			this.fencealarms[fa.Imei] = fa;
		}

		Object.keys(this.fencealarm2s).forEach((imei) => {
			delete this.fencealarm2s[imei];
		});

		for (const fa2 of devices.Fencealarm2s) {
			fa2.Metadata.Tags = sortedSetAdd(fa2.Metadata.Tags, "FenceAlarm2");
			fa2.Metadata.Tags = sortedSetAdd(
				fa2.Metadata.Tags,
				fa2.Metadata.OwnerEmail,
			);
			this.fencealarm2s[fa2.Imei] = fa2;
		}

		Object.keys(this.smartplugs).forEach((imei) => {
			delete this.smartplugs[imei];
		});

		for (const sp of devices.Smartplugs) {
			sp.Metadata.Tags = sortedSetAdd(sp.Metadata.Tags, "SmartPlug");
			sp.Metadata.Tags = sortedSetAdd(
				sp.Metadata.Tags,
				sp.Metadata.OwnerEmail,
			);
			this.smartplugs[sp.Imei] = sp;
		}

		Object.keys(this.smartplug2s).forEach((imei) => {
			delete this.smartplug2s[imei];
		});

		for (const sp2 of devices.Smartplug2s) {
			sp2.Metadata.Tags = sortedSetAdd(sp2.Metadata.Tags, "SmartPlug2");
			sp2.Metadata.Tags = sortedSetAdd(
				sp2.Metadata.Tags,
				sp2.Metadata.OwnerEmail,
			);
			this.smartplug2s[sp2.Imei] = sp2;
		}
	};

	setToken = async (token: Token): Promise<void> => {
		await Storage.setItem("token", JSON.stringify(token));
	};

	setUser = async (user: User): Promise<void> => {
		await Storage.setItem("user", JSON.stringify(user));
	};

	signOut = async (): Promise<void> => {
		await Storage.removeItem("token");
		await Storage.removeItem("user");
	};

	startDevicePolling = (callback: () => void): void => {
		if (this.devicePollingInterval) return;
		const job = (): Promise<void> => this.refreshDevices().then(callback);
		this.devicePollingInterval = setInterval(job, 5000);
	};

	startImpersonating = async (user: User): Promise<void> => {
		sessionStorage.setItem("impersonate", JSON.stringify(user));
		await this.refreshDevices();
	};

	stopDevicePolling = (): void => {
		clearInterval(this.devicePollingInterval);
		this.devicePollingInterval = undefined;
	};

	stopImpersonating = async (): Promise<void> => {
		sessionStorage.removeItem("impersonate");
		await this.refreshDevices();
	};

	smartplug2s: Record<string, Smartplug2> = {};
	smartplugs: Record<string, Smartplug> = {};
}

export const state = new State();
